// ── Participant detail ("/participants/:id") — the participant record (Wave 4) ─
// CONTRACT CHECK: the registry has NO `participants.detail` route — only
// participants.list (GET /participants, a bare array). So this page sources the
// record by finding the id within the scoped list read; it does NOT invent a
// detail endpoint. The server scopes the list per role, so a participant that is
// out of the caller's scope simply isn't in the list -> honest "not found"
// (never a cross-record leak). The id is a STRING — passed VERBATIM, never
// Number()'d.
//
// ── PARTICIPANT ISOLATION (blueprint §6 gate #1) ────────────────────────────
// The route manifest excludes `participant` from /participants/:id
// (operationsRoles). This page ALSO fail-closes (canAccessParticipantsModule):
// a participant who somehow reached it renders ONLY permission-denied with NO
// record — they never see another participant's profile/history here.
//
// ROLE-GATING: SA/Admin get Edit + Complete profile + Remove from the header
// (participants:update); coach/company_admin/cohort_lead are read-only (no write
// affordances). The access/history section is a READ for every staff role.

import { Badge, Button, DetailSurface, useToast } from "@vjal/ui";
import { useState } from "react";
import { Link } from "react-router-dom";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { destructiveButtonClass } from "../../components/ui-classes";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import { useParticipantLifecycleMutations } from "../../hooks/useParticipantLifecycleMutations";
import { evaluateActionCapability } from "../../domain/capabilities";
import { liveRecordIdFromPath } from "../product-live-rows";
import type { CoachingOperationResult, CoachingParticipantDto, ListResponse } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import {
  canAccessParticipantsModule,
  isRemovedParticipant,
  isValidParticipantId,
  participantDisplayName,
  participantStatusLabel,
  participantStatusTone,
  orDash
} from "./participants-helpers";
import { ParticipantFormModal, type ParticipantFormValues } from "./participant-form-modal";
import { ParticipantRemoveModal } from "./participant-remove-modal";
import { ParticipantAccessHistorySection } from "./participant-detail/participant-access-history-section";

const services = getCoachingServices();

export function ParticipantDetailPage({ role, routePath }: ProductScreenContext) {
  const toast = useToast();
  const lifecycle = useParticipantLifecycleMutations();
  const allowUpdate = evaluateActionCapability(role, "participants:update").allowed;

  const participantId = liveRecordIdFromPath(routePath, "participants");
  const validId = isValidParticipantId(participantId);
  const moduleDenied = !canAccessParticipantsModule(role);

  const [editOpen, setEditOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);

  // Request the exact record instead of assuming it is in the first page. The
  // server keeps scope predicates while applying this id filter.
  const query = useHonestQuery<ListResponse<CoachingParticipantDto>>(
    coachingQueryKeys.list("participants.list", { id: participantId ?? "" }),
    () => services.participants.list({ id: participantId ?? undefined, page: 1, pageSize: 1, include_removed: "true" }),
    { enabled: validId && !moduleDenied }
  );
  const participant = query.data?.items.find((row) => row.id === participantId) ?? null;

  // ── Fail closed: denied role -> permission-denied, NO read renders. ─────────
  if (moduleDenied) {
    return (
      <section aria-label="Participant detail" className="col-span-12" data-testid="participant-detail-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            title="You don't have access to this participant record"
            message="Participant records are for coaching staff. Your own details are available on your dashboard."
          />
        </div>
      </section>
    );
  }

  function submitEdit(values: ParticipantFormValues) {
    return runProfileWrite(values).then((updated) => {
      setEditOpen(false);
      return updated;
    });
  }

  function submitComplete(values: ParticipantFormValues) {
    // Complete-profile is the SAME PATCH, pre-populated + preserving unchanged
    // fields (the form seeds from the participant). The page closes the complete
    // modal on success.
    return runProfileWrite(values).then((updated) => {
      setCompleteOpen(false);
      return updated;
    });
  }

  function runProfileWrite(values: ParticipantFormValues) {
    if (!participant) return Promise.reject(new Error("No participant loaded"));
    const cohortId = participant.cohort_id;
    if (!cohortId) return Promise.reject(new Error("Participant has no cohort to update against"));
    return lifecycle.update
      .mutateAsync({
        cohortId,
        participantId: participant.id,
        body: { name: values.name, status: values.status }
      })
      .then((updated) => {
        lifecycle.update.reset();
        toast.success(`Participant ${updated.name ?? values.name ?? values.email} updated`);
        return updated;
      });
  }

  function submitRemove(): Promise<CoachingOperationResult> {
    if (!participant) return Promise.reject(new Error("No participant loaded"));
    const cohortId = participant.cohort_id;
    if (!cohortId) return Promise.reject(new Error("Participant has no cohort to remove from"));
    return lifecycle.remove.mutateAsync({ cohortId, participantId: participant.id }).then((result) => {
      if (result.ok) {
        setRemoveOpen(false);
        lifecycle.remove.reset();
        toast.success(`Participant ${participant.name ?? participant.email ?? ""} removed`.trim());
      }
      return result;
    });
  }

  // ── Bad/absent id, or a not-found / non-ready posture → one honest body. ────
  if (!validId || query.posture !== null || !participant) {
    const posture = !validId ? "empty" : (query.posture ?? (query.data ? "empty" : "loading"));
    const notFound = posture === "empty";
    return (
      <section aria-label="Participant detail" className="col-span-12" data-testid="participant-detail-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture={posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={notFound ? "Participant not found" : undefined}
            message={
              notFound
                ? "This participant doesn't exist, or they aren't in your workspace. They may have been removed."
                : undefined
            }
            canCreate={notFound}
            createAction={
              notFound ? (
                <Link to="/participants" className="text-link no-underline hover:underline">
                  Back to participants
                </Link>
              ) : undefined
            }
          />
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Participant detail" className="col-span-12 flex flex-col gap-4" data-testid="participant-detail-page">
      {/* F3: real @vjal/ui DetailSurface for Detail-1 participant-detail. */}
      <DetailSurface
        title={
          <span className="flex items-center gap-3">
            {participantDisplayName(participant.name, participant.email)}
            <Badge variant={participantStatusTone(participant.status)}>{participantStatusLabel(participant.status)}</Badge>
          </span>
        }
        actions={
          allowUpdate && !isRemovedParticipant(participant) ? (
            <div className="flex max-w-full min-w-0 flex-wrap gap-2">
              <Button variant="secondary" onClick={() => setEditOpen(true)} aria-label="Edit participant">
                Edit
              </Button>
              <Button variant="secondary" onClick={() => setCompleteOpen(true)} aria-label="Complete participant profile">
                Complete profile
              </Button>
              {participant.cohort_id ? (
                <Button
                  variant="danger"
                  className={destructiveButtonClass}
                  onClick={() => setRemoveOpen(true)}
                  aria-label="Remove participant"
                >
                  Remove
                </Button>
              ) : null}
            </div>
          ) : undefined
        }
        fields={[
          {
            id: "back",
            label: "Directory",
            value: (
              <Link to="/participants" className="text-link no-underline hover:underline">
                ← All participants
              </Link>
            ),
          },
          {
            id: "email",
            label: "Email",
            value: orDash(participant.email),
          },
          {
            id: "cohort",
            label: "Cohort",
            value: orDash(participant.cohort_name),
          },
          {
            id: "company",
            label: "Company",
            value: orDash(participant.company_name),
          },
        ]}
        titleExactName="Participant title">
        <div className="mt-4">
          <ParticipantAccessHistorySection participant={participant} />
        </div>
      </DetailSurface>

      {allowUpdate && (
        <>
          <ParticipantFormModal
            open={editOpen}
            mode="edit"
            participant={participant}
            onClose={() => {
              setEditOpen(false);
              lifecycle.update.reset();
            }}
            onSubmit={submitEdit}
            submitting={lifecycle.update.isPending}
            submitError={lifecycle.update.error}
          />
          <ParticipantFormModal
            open={completeOpen}
            mode="complete"
            participant={participant}
            onClose={() => {
              setCompleteOpen(false);
              lifecycle.update.reset();
            }}
            onSubmit={submitComplete}
            submitting={lifecycle.update.isPending}
            submitError={lifecycle.update.error}
          />
          <ParticipantRemoveModal
            open={removeOpen}
            participant={participant}
            onClose={() => {
              setRemoveOpen(false);
              lifecycle.remove.reset();
            }}
            onConfirm={submitRemove}
            submitting={lifecycle.remove.isPending}
            submitError={lifecycle.remove.error}
          />
        </>
      )}
    </section>
  );
}
